import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FilterCells,
  Button,
  Icon,
  Paragraph,
  useColorTokens
} from "@tvg/design-system";
import { RaceProgram, Promo } from "@tvg/ts-types/Race";
import getOptedInPromos from "@tvg/utils/PromoUtils";
import { getIsLogged } from "@urp/store-selectors";
import { PickViewOptionsEnum } from "../../../types";
import { useIsLiveTv } from "../../../hooks";
import { getPromoUrl, isPromoExternal } from "../../../utils/promos";
import {
  getOldPromosURLs,
  getOptedInPromosStore
} from "../../../store/selectors";
import { Container } from "./styled-components";

interface PicksModifiersProps {
  activeView: PickViewOptionsEnum;
  currentRace: RaceProgram;
  onChangeView: (filter: PickViewOptionsEnum) => void;
  renderRunnerModifier?: (
    currentRace: RaceProgram,
    isPicks?: boolean,
    legIndex?: number
  ) => JSX.Element;
}

const PicksModifiers = ({
  activeView,
  currentRace,
  onChangeView,
  renderRunnerModifier
}: PicksModifiersProps) => {
  const colorTokens = useColorTokens();
  const isLiveTv = useIsLiveTv();
  const isLogged = useSelector(getIsLogged);
  const optedInPromos = useSelector(getOptedInPromosStore);
  const oldPromosURLs = useSelector(getOldPromosURLs);

  const promos: Promo | null = getOptedInPromos(
    currentRace.promos || [],
    optedInPromos,
    isLogged
  );

  const isExternal = isPromoExternal({ promos, oldPromosURLs });

  const onClickPromo = useCallback(() => {
    if (promos) {
      const promoUrl = getPromoUrl(isExternal, promos, currentRace, false);

      if (typeof window !== "undefined") {
        // eslint-disable-next-line security/detect-non-literal-fs-filename
        window.open(promoUrl, "_blank", "noopener,noreferrer");
      }
    }
  }, [currentRace, promos, isExternal]);

  return (
    <Container data-qa-label="picks-modifiers-container">
      <FilterCells
        size="m"
        bg="white.900"
        onChange={(val) => onChangeView(val as PickViewOptionsEnum)}
        filterOptions={[
          { label: "Quick Pick", value: PickViewOptionsEnum.QUICK_PICK },
          { label: "Handicapping", value: PickViewOptionsEnum.HANDICAPPING }
        ]}
        activeValue={activeView}
        qaLabel="pick-view-filter"
        hasShadow
      />
      {activeView === PickViewOptionsEnum.HANDICAPPING &&
        typeof renderRunnerModifier === "function" &&
        renderRunnerModifier(currentRace, true)}
      {activeView === PickViewOptionsEnum.QUICK_PICK && isLiveTv && !!promos && (
        <Button
          size="m"
          qaLabel={promos.isOptedIn ? "promo-button-opt-in" : "promo-button"}
          variant={promos.isOptedIn ? "secondary" : "marketing"}
          onPress={onClickPromo}
        >
          <>
            <Icon
              name="promo"
              size="s"
              mr="space-3"
              lineColor={
                colorTokens.content[promos.isOptedIn ? "positive" : "alert"]
              }
              backgroundColor={
                colorTokens.background[
                  promos.isOptedIn ? "positiveSubtle" : "alertSubtle"
                ]
              }
            />
            <Paragraph qaLabel="promo-button-text" fontFamily="medium">
              Promo
            </Paragraph>
          </>
        </Button>
      )}
    </Container>
  );
};

export default PicksModifiers;
